// ════════════════════════════════════════════════════════════════════
// Action Plan ID constants
//
// Source of truth for standard → planId mapping. The BE returns these
// from GET /api/actionplan/plans, but for pages that are standard-specific
// (e.g. /sustainable, /sustainable-report — both GSTC) we hardcode the
// id here to skip the extra round-trip.
//
// If the BE ever reassigns standard → id mapping, update only this file.
// ════════════════════════════════════════════════════════════════════

export const PLAN_IDS = {
  GSTC:  1,
  BCORP: 2,
  // ISO: 3,   // reserved for future
};

// ════════════════════════════════════════════════════════════════════
// Subscription tiers / plan gating.
//
// El valor de `subscriptionType` (slug del producto del BE) puede cambiar
// de nombre o agregarse productos nuevos (premium, cohort_premium,
// maintenance, etc.). Para NO tener que actualizar el FE cada vez, la lógica
// se invierte: en vez de listar todos los planes con acceso, se listan solo
// los RESTRINGIDOS. Todo lo demás (incl. planes desconocidos) = acceso cohort.
//
// Ideal a futuro: que el BE mande un flag/tier canónico (ej. company.tier)
// y el FE no dependa de los slugs de marketing.
// ════════════════════════════════════════════════════════════════════

// Planes SIN acceso completo. Agregar acá solo un plan NUEVO por debajo de
// cohort; los planes full-access nuevos no requieren tocar el FE.
export const RESTRICTED_PLANS = ['baseline', 'launchpad'];

// Acceso nivel cohort = existe un plan y no está en la lista de restringidos.
export const hasCohortAccess = (subscriptionType) =>
  !!subscriptionType && !RESTRICTED_PLANS.includes(subscriptionType);

// Escala ascendente conocida (para orden/CTAs y gating por nivel).
// `baseline` = solo las evaluaciones ASIGNADAS y sus reportes.
// `launchpad` y `cohort` conservan exactamente el acceso que ya tenían.
// cohort_premium y maintenance quedan por encima de cohort.
export const PLAN_TIERS = ['baseline', 'launchpad', 'cohort', 'cohort_premium', 'maintenance']; // next: 'enterprise'

// El piso de la escala. Una empresa sin `subscriptionType` cae acá.
//
// ⚠️ Antes el default de hecho era launchpad (un `null` no era "restringido a
// baseline" porque baseline no existía, y todo el gating preguntaba solo por
// cohort). Al bajar el piso, una cuenta LEGACY con `subscriptionType: null`
// pierde acceso que hoy tiene. El BE debe hacer backfill —poner 'launchpad'
// explícito en las cuentas existentes— ANTES de que esto llegue a producción;
// así el piso nuevo aplica solo a cuentas nuevas.
export const DEFAULT_TIER = 'baseline';

// Índice del plan en la escala. Un plan desconocido y NO restringido se asume
// a nivel cohort (acceso completo), para que un rename de marketing no bloquee
// a nadie; uno desconocido Y restringido cae al piso.
export const tierIndex = (subscriptionType) => {
  if (!subscriptionType) return PLAN_TIERS.indexOf(DEFAULT_TIER);
  const i = PLAN_TIERS.indexOf(subscriptionType);
  if (i !== -1) return i;
  return RESTRICTED_PLANS.includes(subscriptionType)
    ? PLAN_TIERS.indexOf(DEFAULT_TIER)
    : PLAN_TIERS.indexOf('cohort');
};

// True cuando la suscripción está en el tier pedido o por encima.
// El gate 'cohort' se resuelve con hasCohortAccess (robusto a slugs
// desconocidos); el resto usa la escala conocida.
export const hasTier = (minTier, subscriptionType) =>
  (minTier === 'cohort')
    ? hasCohortAccess(subscriptionType)
    : tierIndex(subscriptionType) >= tierIndex(minTier);

// El tier canónico de una suscripción — el nombre del escalón, no un booleano.
// Es lo que permite preguntar "¿en qué escalón estoy?" en vez de "¿soy cohort
// o no?", que con tres tiers ya no alcanza.
export const planTier = (subscriptionType) => PLAN_TIERS[tierIndex(subscriptionType)] ?? DEFAULT_TIER;

// El escalón siguiente, para los CTA de upgrade. null si ya está arriba de todo
// lo que se vende (cohort y superiores no tienen a dónde subir hoy).
export const nextTier = (subscriptionType) => {
  const i = tierIndex(subscriptionType);
  return i < PLAN_TIERS.indexOf('cohort') ? PLAN_TIERS[i + 1] : null;
};
